import {
    MembershipPaymentPlan,
    Product,
    ProductInstance,
    TicketOptionWithQuantity,
    VatBreakdown,
} from '@repo/types';
import { getCartItemId } from '@repo/widget-utils/cart/cartUtils';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';
import { useMemo } from 'react';
import { useBookingContext } from 'src/widgets/BookingContext';

export default function usePriceQuantities(
    product: Product | null,
    selectedProducts: ProductInstance[] | undefined,
    quantities: TicketOptionWithQuantity[],
) {
    const { cartItems, newCartItem, selectedTimeSlot } = useBookingContext();

    const newCartItemFromContext = useMemo(() => {
        const newCartItemFromContext = newCartItem
            ? cartItems.find((item) => getCartItemId(item) === getCartItemId(newCartItem))
            : null;

        return newCartItemFromContext;
    }, [cartItems, newCartItem]);

    const paymentPlan = useMemo(() => {
        if (!newCartItemFromContext || !quantities || !selectedProducts) return undefined;

        const foundPaymentPlans = newCartItemFromContext.ticketOptions.flatMap(
            (to) => to.membership ?? [],
        );
        return foundPaymentPlans.length > 0 ? foundPaymentPlans : undefined;
    }, [newCartItemFromContext, quantities, selectedProducts]);

    return useMemo(() => {
        if (!selectedProducts) return [];

        if (selectedProducts.length < 2) {
            const mapped = mapForZeroOrOne(quantities, selectedProducts, paymentPlan);

            // A multi-slot timeslot session books N consecutive slots but is still
            // represented by a single product instance, so `mapped` only holds the
            // price of one minimum unit. Scale price + VAT by the number of session
            // units so the preview and cart match what Base charges. Membership prices
            // come from the payment plan, so leave those untouched.
            if (!paymentPlan) {
                const sessionUnits = getTimeslotSessionUnits(
                    product,
                    selectedProducts,
                    selectedTimeSlot,
                );
                return scaleQuantitiesForSessionUnits(mapped, sessionUnits);
            }

            return mapped;
        } else if (paymentPlan && paymentPlan.length > 0) {
            return mapForPaymentPlan(quantities, selectedProducts, paymentPlan);
        }

        return mapForMultipleWithoutPaymentPlan(quantities, selectedProducts, product);
    }, [paymentPlan, quantities, selectedProducts, product, selectedTimeSlot]);
}

/**
 * Number of "session units" a timeslot selection represents — how many times the
 * product's minimum booking quantity fits into the chosen session length. A default
 * booking is 1; the session-length selector picks 2× or 3×. Returns 1 for
 * non-timeslot products and whenever the data needed to compute it is missing.
 */
export function getTimeslotSessionUnits(
    product: Product | null,
    selectedProducts: ProductInstance[] | undefined,
    selectedTimeSlot: TimeSlotType | undefined,
): number {
    if (product?.type !== 'timeslot') return 1;

    const slotCount = selectedTimeSlot?.products?.length ?? 0;
    const minPerUnit = selectedProducts?.[0]?.minProductsRequiredToBook ?? 1;
    if (slotCount <= 0 || minPerUnit <= 0) return 1;

    const units = Math.round(slotCount / minPerUnit);
    return units > 1 ? units : 1;
}

/**
 * Multiply price, deposit and VAT of each quantity by `units`, leaving the ticket
 * count itself untouched — a 2-hour session still shows "1x Adult", just at 2× price.
 * A no-op (same reference) when `units <= 1`.
 */
export function scaleQuantitiesForSessionUnits(
    quantities: TicketOptionWithQuantity[],
    units: number,
): TicketOptionWithQuantity[] {
    if (units <= 1) return quantities;

    return quantities.map((q) => ({
        ...q,
        price: q.price * units,
        vatAmount: q.vatAmount * units,
        depositAmount: q.depositAmount !== null ? q.depositAmount * units : q.depositAmount,
        vatBreakdown: q.vatBreakdown.map((vb) => ({
            ...vb,
            amount: vb.amount * units,
            vatAmount: vb.vatAmount * units,
        })),
    }));
}

export function findProductInstancesForTicketOption(
    ticketOption: TicketOptionWithQuantity,
    selectedProducts: ProductInstance[],
) {
    return selectedProducts.flatMap((p) =>
        p.ticketOptions
            .filter((to) => to.ticketCategoryId === ticketOption.ticketCategoryId)
            .flatMap((to) => to.productInstances),
    );
}

export function findPaymentPlanForTicketOption(
    ticketOption: TicketOptionWithQuantity,
    paymentPlan: MembershipPaymentPlan[] | undefined,
) {
    return paymentPlan?.filter(
        (p) =>
            p.ticket.defaultTicketOptionId.toString() === ticketOption.defaultId ||
            p.ticket.defaultTicketOptionId.toString() === ticketOption.id,
    );
}

export function findPaymentPlanForTicketOptionWithPaymentPlanAndSelectedProducts(
    ticketOption: TicketOptionWithQuantity,
    paymentPlan: MembershipPaymentPlan[],
    selectedProducts: ProductInstance[],
) {
    return paymentPlan.filter(
        (p) =>
            selectedProducts
                .flatMap((p) => p.ticketOptions)
                .find((to) => to.id === p.ticket.defaultTicketOptionId.toString())?.defaultId ===
            ticketOption.defaultId,
    );
}

export function mapForZeroOrOne(
    quantities: TicketOptionWithQuantity[],
    selectedProducts: ProductInstance[],
    paymentPlan: MembershipPaymentPlan[] | undefined,
) {
    return quantities
        .map((ticketOption) => ({
            ...ticketOption,
            // Extract the product instances from the matching ticket option,
            // in case the ticket options are merged from multiple products (using projectColletionId)
            // and we need to link the correct product instances to the ticket option
            productInstances: findProductInstancesForTicketOption(ticketOption, selectedProducts),
            membership: findPaymentPlanForTicketOption(ticketOption, paymentPlan),
        }))
        .filter((ticketOption) => ticketOption.productInstances.length > 0);
}

export function mapForPaymentPlan(
    quantities: TicketOptionWithQuantity[],
    selectedProducts: ProductInstance[],
    paymentPlan: MembershipPaymentPlan[],
) {
    return quantities.map((ticketOption) => ({
        ...ticketOption,
        // Extract the product instances from the matching ticket option,
        // in case the ticket options are merged from multiple products (using projectColletionId)
        // and we need to link the correct product instances to the ticket option
        productInstances: findProductInstancesForTicketOption(ticketOption, selectedProducts),
        membership: findPaymentPlanForTicketOptionWithPaymentPlanAndSelectedProducts(
            ticketOption,
            paymentPlan,
            selectedProducts,
        ),
    }));
}

export function mapForMultipleWithoutPaymentPlan(
    quantities: TicketOptionWithQuantity[],
    selectedProducts: ProductInstance[],
    product: Product | null,
) {
    const applicableProducts =
        product?.type === 'nights'
            ? selectedProducts.slice(0, selectedProducts.length - 1)
            : selectedProducts;

    const newQuantities = quantities.slice();

    return newQuantities
        .map((q): TicketOptionWithQuantity => {
            return {
                ...q,
                productInstances: selectedProducts.flatMap((p) =>
                    p.ticketOptions
                        .filter((to) => to.ticketCategoryId === q.ticketCategoryId)
                        .flatMap((to) => to.productInstances),
                ),
                ...applicableProducts.reduce(
                    (acc, cur) => {
                        const ticketOption = cur.ticketOptions.find(
                            (to) => to.ticketCategoryId === q.ticketCategoryId,
                        );
                        acc.price += ticketOption?.price ?? 0;
                        acc.vatAmount += ticketOption?.vatAmount ?? 0;
                        acc.vatBreakdown =
                            ticketOption?.vatBreakdown.reduce((vbAcc, vbCur) => {
                                const matchingRateIdx = vbAcc.findIndex(
                                    (x) => x.rate === vbCur.rate,
                                );
                                if (matchingRateIdx === -1) {
                                    vbAcc.push(vbCur);
                                    return vbAcc;
                                } else {
                                    return [
                                        ...vbAcc.slice(0, matchingRateIdx),
                                        {
                                            ...vbAcc[matchingRateIdx],
                                            amount: vbAcc[matchingRateIdx].amount + vbCur.amount,
                                            vatAmount:
                                                vbAcc[matchingRateIdx].vatAmount + vbCur.vatAmount,
                                        },
                                        ...vbAcc.slice(matchingRateIdx + 1),
                                    ];
                                }
                            }, acc.vatBreakdown) ?? acc.vatBreakdown;
                        return acc;
                    },
                    {
                        price: 0,
                        vatAmount: 0,
                        vatBreakdown: [] as VatBreakdown[],
                    },
                ),
            };
        })
        .filter((q) => q.productInstances.length > 0);
}
